/**
 * map2d.js – Leaflet 2D mode
 * Drives every key in SAT_CONFIG generically.
 */

import L from "leaflet";
import { propagateNow, groundTrackPoints } from "./propagate.js";
import { SAT_CONFIG } from "./satellites.js";

const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

// Emoji icons for single objects; constellations use circleMarkers
const SINGLE_ICONS = { iss: "🛸", hst: "🔭" };

let map = null;
const markers = {};  // flat key → Leaflet layer
const tracks  = {};  // key → [Leaflet polylines]

// ── Init ──────────────────────────────────────────────────
export function initMap2D(containerId) {
  if (map) return;
  map = L.map(containerId, {
    center: [20, 0], zoom: 2, minZoom: 2, maxZoom: 8, zoomControl: true,
  });
  L.tileLayer(TILE_URL, {
    attribution: "© CartoDB", subdomains: "abcd", maxZoom: 19,
  }).addTo(map);
}

// ── Destroy ───────────────────────────────────────────────
export function destroyMap2D() {
  if (!map) return;
  map.remove();
  map = null;
  Object.keys(markers).forEach((k) => delete markers[k]);
  Object.keys(tracks).forEach((k)  => delete tracks[k]);
}

// ── Single-sat marker ─────────────────────────────────────
function makeSingleIcon(key) {
  const emoji = SINGLE_ICONS[key] || "●";
  return L.divIcon({
    className: "",
    html: `<div class="sat-marker ${key}">${emoji}</div>`,
    iconSize: [28, 28], iconAnchor: [14, 14],
  });
}

export function updateSat2D(key, tleEntry) {
  if (!map) return null;
  const pos = propagateNow(tleEntry.tle1, tleEntry.tle2);
  if (!pos) return null;
  const { lat, lon, alt, velocity } = pos;

  if (!markers[key]) {
    markers[key] = L.marker([lat, lon], {
      icon: makeSingleIcon(key), zIndexOffset: 1000,
    })
      .addTo(map)
      .bindTooltip("", {
        permanent: false, direction: "top",
        offset: [0, -16], className: "sat-tooltip",
      });
  } else {
    markers[key].setLatLng([lat, lon]);
  }
  markers[key].setTooltipContent(
    `${tleEntry.name.trim()}<br>Alt ${alt.toFixed(0)} km | ${velocity.toFixed(2)} km/s`
  );
  return { lat, lon, alt, velocity };
}

// ── Ground track (single sats) ────────────────────────────
export function drawGroundTrack2D(key, tleEntry) {
  if (!map) return;
  const cfg = SAT_CONFIG[key];
  if (tracks[key]) tracks[key].forEach((l) => map.removeLayer(l));

  const pts = groundTrackPoints(tleEntry.tle1, tleEntry.tle2, {
    minutesAhead: 90, stepSec: 30,
  });

  const segments = [];
  let current = [];
  pts.forEach((p) => {
    if (p === null) { if (current.length) segments.push(current); current = []; }
    else current.push([p.lat, p.lon]);
  });
  if (current.length) segments.push(current);

  tracks[key] = segments.map((seg) =>
    L.polyline(seg, {
      color: cfg?.cssColor || "#ffffff", weight: 1.5, opacity: 0.5,
    }).addTo(map)
  );
}

// ── Constellation dots on map ─────────────────────────────
export function updateConstellation2D(key, tleList) {
  if (!map) return;
  const cfg   = SAT_CONFIG[key];
  const color = cfg?.cssColor || "#4ade80";

  tleList.forEach((tle, i) => {
    const mkey = `${key}_${i}`;
    const pos  = propagateNow(tle.tle1, tle.tle2);
    if (!pos) return;

    if (!markers[mkey]) {
      markers[mkey] = L.circleMarker([pos.lat, pos.lon], {
        radius: 3, color, fillColor: color,
        fillOpacity: 0.75, weight: 0.5, opacity: 0.6,
      })
        .addTo(map)
        .bindTooltip(tle.name.trim(), { className: "sat-tooltip", direction: "top" });
    } else {
      markers[mkey].setLatLng([pos.lat, pos.lon]);
    }
  });
}

// ── Focus ─────────────────────────────────────────────────
export function focusSat2D(key) {
  if (map && markers[key])
    map.flyTo(markers[key].getLatLng(), 4, { duration: 1.2 });
}

// ─────────────────────────────────────────────────────────
//  Constellation sidebar — compact grouped cards
// ─────────────────────────────────────────────────────────

/**
 * Build the full constellation panel from tleData.
 * One "card" per constellation key in SAT_CONFIG.
 */
export function buildConstellationPanel(tleData) {
  const container = document.getElementById("constellation-panel");
  if (!container) return;
  container.innerHTML = "";

  const constKeys = Object.keys(SAT_CONFIG).filter(
    (k) => SAT_CONFIG[k].type === "constellation"
  );
  if (!constKeys.length) return;

  constKeys.forEach((key) => {
    const cfg  = SAT_CONFIG[key];
    const sats = tleData[key] || [];

    // ── Card shell ─────────────────────────────────────
    const card = document.createElement("div");
    card.className  = "const-card";
    card.id         = `const-card-${key}`;
    card.style.setProperty("--cc", cfg.cssColor);

    // ── Card header ────────────────────────────────────
    const hdr = document.createElement("div");
    hdr.className = "const-card-hdr";
    hdr.innerHTML = `
      <div class="const-card-left">
        <span class="const-led" id="const-led-${key}"></span>
        <span class="const-card-name">${cfg.label}</span>
      </div>
      <div class="const-card-right">
        <span class="const-card-count" id="const-count-${key}">
          <span id="const-active-${key}">0</span>/${sats.length}
        </span>
        <span class="const-card-toggle" id="const-toggle-${key}" title="Toggle dots">▾</span>
      </div>
    `;
    card.appendChild(hdr);

    // ── Collapsible dot grid ───────────────────────────
    const body = document.createElement("div");
    body.className = "const-card-body";
    body.id        = `const-body-${key}`;

    sats.forEach((sat, i) => {
      const short = _shortName(key, sat.name);
      const dot   = document.createElement("div");
      dot.className = "const-dot";
      dot.id        = `const-dot-${key}-${i}`;
      dot.title     = sat.name.trim();
      dot.innerHTML = `
        <div class="const-dot-led"></div>
        <span class="const-dot-lbl">${short}</span>
      `;
      body.appendChild(dot);
    });

    card.appendChild(body);
    container.appendChild(card);

    // Toggle collapse on header click
    hdr.addEventListener("click", () => {
      const open = !body.classList.contains("collapsed");
      body.classList.toggle("collapsed", open);
      document.getElementById(`const-toggle-${key}`).textContent = open ? "▸" : "▾";
    });
  });
}

/**
 * Refresh dot states (active/inactive) and active count.
 * Called every tick.
 */
export function refreshConstellationDots(tleData) {
  const constKeys = Object.keys(SAT_CONFIG).filter(
    (k) => SAT_CONFIG[k].type === "constellation"
  );
  constKeys.forEach((key) => {
    const sats      = tleData[key] || [];
    let   activeCount = 0;

    sats.forEach((sat, i) => {
      const dot = document.getElementById(`const-dot-${key}-${i}`);
      if (!dot) return;
      const ok = !!propagateNow(sat.tle1, sat.tle2);
      dot.classList.toggle("inactive", !ok);
      if (ok) activeCount++;
    });

    // Update count label
    const countEl = document.getElementById(`const-active-${key}`);
    if (countEl) countEl.textContent = activeCount;

    // LED on card header: green if >0, dim otherwise
    const led = document.getElementById(`const-led-${key}`);
    if (led) led.classList.toggle("on", activeCount > 0);
  });
}

// ── Name shortener ────────────────────────────────────────
function _shortName(key, raw) {
  const s = raw.trim();
  switch (key) {
    case "gps":
      return s.replace(/GPS BIIR-?M?/i, "RM").replace(/GPS BIIF/i, "F")
               .replace(/GPS III-?F?/i, "3F").replace(/GPS III/i, "G3")
               .replace(/GPS /i, "").slice(0, 6);
    case "glonass":
      return s.replace(/COSMOS-?/i, "").replace(/\s*\(.*\)/, "").slice(0, 6);
    case "galileo":
      return s.replace(/GALILEO-?/i, "GAL").replace(/\s/g, "").slice(0, 6);
    case "beidou":
      return s.replace(/BEIDOU-?/i, "BDS").replace(/\s/g, "").slice(0, 6);
    default:
      return s.slice(0, 6);
  }
}